import {
  addMonths,
  ageInYears,
  compareDates,
  toIso,
  toSimpleDate
} from "./chunk-QW2F5SQ5.js";

// src/travel/index.ts
function validForTravel(result, options) {
  const months = options.minMonthsValidity ?? 6;
  const travel = toSimpleDate(options.travelDate);
  const ret = options.returnDate != null ? toSimpleDate(options.returnDate) : null;
  const expiry = toSimpleDate(result.fields.expiryDate);
  const base = {
    expiryDate: expiry ? toIso(expiry) : null,
    travelDate: travel ? toIso(travel) : null,
    returnDate: ret ? toIso(ret) : null,
    mrzValid: result.valid
  };
  if (!travel || options.returnDate != null && !ret || ret && compareDates(ret, travel) < 0) {
    return {
      ...base,
      ok: false,
      rule: "invalid_travel_dates",
      requiredUntil: null,
      message: !travel ? "The travel date is missing or invalid (use YYYY-MM-DD)." : !ret ? "The return date is invalid (use YYYY-MM-DD)." : `The return date ${toIso(ret)} is before the travel date ${toIso(travel)}.`
    };
  }
  if (!expiry) {
    return {
      ...base,
      ok: false,
      rule: "expiry_unknown",
      requiredUntil: null,
      message: "The expiry date could not be read from the MRZ."
    };
  }
  const anchor = ret ?? travel;
  const anchorLabel = ret ? "return date" : "travel date";
  const required = addMonths(anchor, months);
  const e = toIso(expiry);
  if (compareDates(expiry, travel) < 0) {
    return {
      ...base,
      ok: false,
      rule: "expired_before_travel",
      requiredUntil: toIso(required),
      message: `Expires ${e}, which is before the travel date ${toIso(travel)}.`
    };
  }
  const plural = months === 1 ? "month" : "months";
  if (compareDates(expiry, required) < 0) {
    return {
      ...base,
      ok: false,
      rule: "min_validity",
      requiredUntil: toIso(required),
      message: `Expires ${e}, which is less than ${months} ${plural} after ${anchorLabel} ${toIso(anchor)}.`
    };
  }
  return {
    ...base,
    ok: true,
    rule: "min_validity",
    requiredUntil: toIso(required),
    message: `Expires ${e}, at least ${months} ${plural} after ${anchorLabel} ${toIso(anchor)} (required until ${toIso(required)}).`
  };
}
function typeForAge(age, infantUnder, childUnder) {
  if (age < infantUnder) return "INF";
  if (age < childUnder) return "CHD";
  return "ADT";
}
function passengerType(dob, travelDate, options = {}) {
  const infantUnder = options.infantUnder ?? 2;
  const childUnder = options.childUnder ?? 12;
  const birth = toSimpleDate(dob ?? null);
  const travel = toSimpleDate(travelDate);
  const ret = options.returnDate != null ? toSimpleDate(options.returnDate) : null;
  const empty = { type: null, age: null, returnType: null, returnAge: null, changes: false };
  if (!birth) return { ...empty, note: "Date of birth is missing or invalid." };
  if (!travel) return { ...empty, note: "Travel date is missing or invalid." };
  const age = ageInYears(birth, travel);
  if (age < 0) return { ...empty, note: "Date of birth is after the travel date." };
  const type = typeForAge(age, infantUnder, childUnder);
  if (!ret) return { type, age, returnType: null, returnAge: null, changes: false, note: null };
  const returnAge = ageInYears(birth, ret);
  const returnType = typeForAge(returnAge, infantUnder, childUnder);
  const changes = returnType !== type;
  return {
    type,
    age,
    returnType,
    returnAge,
    changes,
    note: changes ? `Passenger is ${type} (age ${age}) on ${toIso(travel)} but ${returnType} (age ${returnAge}) on return ${toIso(ret)}.` : null
  };
}
var DEFAULT_TITLES = {
  adultMale: "MR",
  adultFemale: "MS",
  childMale: "MSTR",
  childFemale: "MISS",
  infantMale: "MSTR",
  infantFemale: "MISS"
};
function airlineName(result, options = {}) {
  const warnings = [];
  const f = result.fields;
  let surname = (f.surname ?? "").trim();
  if (options.removeSurnameSpaces) surname = surname.replace(/\s+/g, "");
  let given = (f.givenNames ?? "").trim();
  if (!surname) warnings.push("No surname in the MRZ.");
  if (!given) {
    const mode = options.missingGivenName ?? "empty";
    if (mode === "FNU") given = "FNU";
    else if (mode === "repeat-surname") given = surname;
    warnings.push(
      "No given names in the MRZ; check the airline\u2019s rule for single-name passengers."
    );
  }
  if (f.nameTruncated) {
    warnings.push(
      "The MRZ name field is full, so the name may be truncated. Use the full name from the visual zone."
    );
  }
  let title = null;
  if (options.includeTitle !== false) {
    const rules = { ...DEFAULT_TITLES, ...options.titles };
    const pt = passengerType(f.birthDate, options.travelDate ?? /* @__PURE__ */ new Date(), options);
    const group = pt.type === "INF" ? "infant" : pt.type === "CHD" ? "child" : "adult";
    if (!pt.type) warnings.push(`Title based on adult rules: ${pt.note}`);
    if (f.sex === "M") title = rules[`${group}Male`];
    else if (f.sex === "F") title = rules[`${group}Female`];
    else warnings.push("Sex is not M or F, so no title was added.");
  }
  const compose = () => `${surname}/${[given, title].filter(Boolean).join(" ")}`;
  let name = compose();
  if (options.maxLength && name.length > options.maxLength) {
    const room = options.maxLength - surname.length - 1 - (title ? title.length + 1 : 0);
    given = room > 0 ? given.slice(0, room).trim() : "";
    name = compose().slice(0, options.maxLength);
    warnings.push(`Name shortened to ${options.maxLength} characters.`);
  }
  return { name, surname, givenNames: given, title, warnings };
}
function toPassenger(result, options = {}) {
  const f = result.fields;
  const an = airlineName(result, options);
  const pt = passengerType(f.birthDate, options.travelDate ?? /* @__PURE__ */ new Date(), options);
  const warnings = [...an.warnings];
  if (!result.valid)
    warnings.push("The MRZ did not validate; check every field against the document.");
  if (pt.note && !warnings.some((w) => w.includes(pt.note))) warnings.push(pt.note);
  return {
    surname: f.surname,
    givenNames: f.givenNames,
    airlineName: an.name,
    title: an.title,
    dateOfBirth: f.birthDate,
    sex: f.sex,
    nationality: f.nationality,
    nationalityName: f.nationalityName,
    documentType: f.documentType,
    passportNumber: f.documentNumber,
    issuingCountry: f.issuingState,
    issuingCountryName: f.issuingStateName,
    expiryDate: f.expiryDate,
    passengerType: pt.type,
    mrzValid: result.valid,
    warnings
  };
}
export {
  DEFAULT_TITLES,
  airlineName,
  passengerType,
  toPassenger,
  validForTravel
};
//# sourceMappingURL=travel.js.map